import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  theme
} from 'investira.react.components';
import { PessoaImage } from '../../../components/molecules';
import helpers from '../../../utils/helpers';
import { buildComparadorFundosResultChips } from '../_shared/comparadorFundosResultChips';
import ComparadorFundosVirtuosoList from './ComparadorFundosVirtuosoList';
import { formats } from 'investira.sdk';

const wVirtuosoComponents = {
  Item: 'li',
  List: ComparadorFundosVirtuosoList
};

/**
 * Lista virtualizada dos fundos encontrados e seus estados de carregamento.
 */
const ComparadorFundosResultsList = memo(
  ({
    error,
    filters,
    hasMore,
    loading,
    loadingMore,
    loadMoreError,
    onFocusItem,
    onLoadMore,
    onRetryLoadMore,
    onSelectItem,
    results,
    selectedFundosIds,
    sort,
    totalItems,
    sortComponent
  }) => {
    if (loading) {
      return (
        <Stack>
          {/* <Typography role="status">Carregando fundos...</Typography> */}
          <List aria-label="Carregando fundos">
            {[...Array(10)].map((_, xIndex) => (
              <ListItemButton key={xIndex} disabled>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="rectangular" width="100%" height={24} />
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Stack>
      );
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    if (!results.length) {
      return (
        <Stack justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
          <Typography variant="caption" color="textPrimary" role="status">
            Nenhum resultado encontrado.
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack sx={{ height: { xs: 360, md: '100%' }, minHeight: 0 }}>
        <Stack>
          <Stack id="results-count-area" sx={{ px: 2, pb: 1 }} direction="row" alignItems="center">
            <Stack id="count-area" flexGrow={1}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {formats.formatNumber(totalItems, 0, true)} resultados encontrados
              </Typography>
            </Stack>
            <Stack id="sort">{sortComponent}</Stack>
          </Stack>
          <Divider />
        </Stack>
        <Virtuoso
          aria-label="Resultados de fundos"
          role="region"
          style={{ flex: 1 }}
          data={results}
          components={wVirtuosoComponents}
          computeItemKey={(_pIndex, pItem) => pItem.id}
          defaultItemHeight={48}
          increaseViewportBy={{ top: 0, bottom: 600 }}
          endReached={hasMore && !loadingMore ? onLoadMore : undefined}
          itemContent={(_pIndex, pItem) => {
            const isSelected = selectedFundosIds.includes(pItem.id);
            const xResultChips = buildComparadorFundosResultChips(filters, pItem, sort);

            return (
              <ListItemButton
                sx={{
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderBottom: theme => `1px solid ${theme.palette.secondary.main}`
                }}
                component="div"
                aria-label={`Selecionar ${helpers.getAtivoDescricao(pItem)}`}
                onMouseEnter={() => onFocusItem(pItem.id)}
                onFocus={() => onFocusItem(pItem.id)}
                onClick={() => onSelectItem(pItem)}
                secondaryAction={
                  isSelected ? (
                    <Icon iconName="option_select" size={21} color="primary" />
                  ) : (
                    <Stack>
                      <IconButton
                        color="primary"
                        component={Link}
                        to={`/ativos/fundos/${pItem.id}`}
                        rel="search"
                        onClick={pEvent => pEvent.stopPropagation()}
                        target="_blank">
                        <Icon iconName="tab_link" size={18} />
                      </IconButton>
                    </Stack>
                  )
                }>
                <ListItemAvatar>
                  <PessoaImage
                    logoPessoaId={pItem.logoPessoaId}
                    alt={pItem.pessoa}
                    hasLogo={pItem.logo_ok}
                    size={26}
                  />
                </ListItemAvatar>
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                  <Typography variant="caption">{helpers.getAtivoDescricao(pItem)}</Typography>
                  {xResultChips.length ? (
                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                      {xResultChips.map(pChip => (
                        <Chip
                          key={pChip.key}
                          label={pChip.label}
                          size="small"
                          variant="contained"
                          color="secondary"
                          sx={{
                            height: 20,
                            '& .MuiChip-label': {
                              px: 0.75,
                              fontSize: '0.6875rem'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </ListItemButton>
            );
          }}
        />
        {loadingMore ? (
          <Stack direction="row" alignItems="center" justifyContent="center" role="status">
            <Stack
              direction="row"
              gap={2}
              sx={{
                py: 1.5,
                px: 4,
                position: 'fixed',
                bottom: '48px',
                backgroundColor: 'background.dark',
                borderRadius: 6
              }}>
              <CircularProgress size={20} />
              <Typography variant="caption">Carregando mais fundos...</Typography>
            </Stack>
          </Stack>
        ) : null}
        {loadMoreError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetryLoadMore}>
                Tentar novamente
              </Button>
            }>
            {loadMoreError}
          </Alert>
        ) : null}
      </Stack>
    );
  }
);

export default ComparadorFundosResultsList;
